<!--
  ColorFilmstrip - horizontal swatch strip below the persistent Stage.
  Canonical product editor.

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 3 - ColorFilmstrip + VariantMenu)

  Day 3 design alignment (Group B, 2026-06-16) - aligns with the 2026-06-13
  Standalone.html design:
    - Header row above the swatch strip: eyebrow text "Colors on this
      product · click to preview · use the badge to set its variant"
      with a palette ligature, and a right-aligned "Manage colors"
      stub button (no-op for the interim build).
    - Swatch body: 34x34, thin border, rounded corners. The currently-
      previewing swatch shows a checkmark ligature overlay and a
      brand-color outline.
    - Variant badge centered ON the swatch (bottom-right anchor matching
      design) and is the MENU TRIGGER — clicking the badge opens
      VariantMenu, NOT a separate kebab. The separate kebab is gone.
    - Always visible: the filmstrip root renders whether or not a
      placement is currently selected. In Properties mode the header
      and swatches remain discoverable; routing edits still write to
      the same store.

  Click semantics:
    - Click swatch body  -> editorUi.setSelectedColor (preview)
    - Click badge        -> open/close VariantMenu (routing) — does NOT
                            select the color (click.stop)
-->
<template>
    <div data-testid="color-filmstrip-root" class="color-filmstrip">
        <div data-testid="filmstrip-header-root" class="filmstrip-header">
            <span class="filmstrip-header-icon material-icons" aria-hidden="true"
                >palette</span
            >
            <span
                data-testid="filmstrip-header-label"
                class="filmstrip-header-label"
            >
                Colors on this product
                <span class="filmstrip-header-sep">·</span>
                click to preview
                <span class="filmstrip-header-sep">·</span>
                use the badge to set its variant
            </span>
            <!--
              Day 4 round-1 P2 follow-up: disable the no-op "Manage
              colors" CTA until a future phase wires the real navigation to
              the Properties > Colors editor. Same pattern as the
              Day 2 EditorPagehead hide-pill and the Day 5
              ReviewBanner CTA — the surface stays visible so the
              design intent is preserved, but a click cannot mislead
              a viewer into thinking something happened.
            -->
            <button
                type="button"
                data-testid="filmstrip-manage-colors"
                class="filmstrip-manage-colors"
                disabled
                title="Wires in Phase 1 (real navigation to the Properties > Colors editor is not yet in this repo)."
                @click="onManageColors"
            >
                Manage colors
                <span class="material-icons" aria-hidden="true">tune</span>
            </button>
        </div>
        <div class="filmstrip-dots">
            <!--
              Day 3 gate round-1 P1 (a11y): the swatch is a real
              <button> and the variant badge is a sibling <button>
              positioned absolutely over the swatch's bottom-right
              corner. Previously the badge was nested inside the
              swatch, which is a button-in-button HTML / ARIA
              violation. Sibling structure gives screen readers two
              clean focus targets with distinct labels.
            -->
            <div
                v-for="color in selectedColors"
                :key="color.color_sku"
                class="filmstrip-cell"
            >
                <button
                    type="button"
                    :data-testid="`filmstrip-swatch-${color.color_sku}`"
                    :data-variant-kind="kindFor(color) || ''"
                    :data-artifi-unsupported="
                        isColorUnsupported(color) ? 'true' : 'false'
                    "
                    :aria-pressed="
                        editorUi.selectedColorSku === color.color_sku
                            ? 'true'
                            : 'false'
                    "
                    :aria-label="swatchAriaLabel(color)"
                    :data-hex-fallback="color.is_hex_fallback ? 'true' : 'false'"
                    :class="{
                        'filmstrip-swatch': true,
                        'is-selected':
                            editorUi.selectedColorSku === color.color_sku,
                        'is-artifi-unsupported': isColorUnsupported(color),
                        'is-hex-fallback': !!color.is_hex_fallback,
                    }"
                    :style="swatchStyle(color)"
                    :title="swatchTitle(color)"
                    @click="onSwatchClick(color.color_sku)"
                >
                    <span
                        v-if="editorUi.selectedColorSku === color.color_sku"
                        :data-testid="`filmstrip-swatch-${color.color_sku}-check`"
                        class="filmstrip-check material-icons"
                        aria-hidden="true"
                        >check</span
                    >
                    <!-- Artifi coverage gap: a slashed-eye overlay tells
                         the user this color has no preview support.
                         Visible only when isColorUnsupported(color) is
                         true AND this swatch is not the currently
                         selected one (the check overlay wins so the
                         user always knows which swatch is active). -->
                    <span
                        v-if="
                            isColorUnsupported(color) &&
                            editorUi.selectedColorSku !== color.color_sku
                        "
                        :data-testid="`filmstrip-swatch-${color.color_sku}-no-preview`"
                        class="filmstrip-no-preview material-icons"
                        aria-hidden="true"
                        >visibility_off</span
                    >
                    <!--
                      NEW (blueprint 5.3, Phase 6): partial-failure
                      indicator -- shown when coverageFootnote is non-null
                      but allFail is false (some views fail, at least one
                      passes or is still unprobed). Distinct, less-alarming
                      corner dot rather than the full slashed-eye overlay.
                      Mutually exclusive with the slashed-eye (allFail vs
                      partial can never both be true for the same color).
                    -->
                    <span
                        v-if="isColorPartiallyUnsupported(color)"
                        :data-testid="`filmstrip-swatch-${color.color_sku}-partial-preview`"
                        class="filmstrip-partial-preview"
                        aria-hidden="true"
                    />
                </button>
                <!--
                  Slice 0 (editable-variant-cards): the badge is now the
                  v-menu ACTIVATOR, passed into VariantMenu's #activator
                  slot. VariantMenu owns the v-menu; its v-model:open is
                  bound to whether THIS color's menu is the open one, so
                  the menu dismisses on outside-click + Escape (v-menu
                  defaults) instead of only via the 'x'. @click.stop still
                  keeps opening the menu from selecting the color; the
                  activator's own onClick (from Vuetify) toggles the menu.
                -->
                <VariantMenu
                    :color-sku="color.color_sku"
                    :hex="color.hex"
                    :name="color.name"
                    :current-kind="kindFor(color)"
                    :current-variant-label="labelFor(color)"
                    :variants="placementVariants"
                    :current-variant-id="currentVariantIdFor(color)"
                    :current-owner-label="ownerLabelFor(color)"
                    :product-colors="selectedColors"
                    :open="openMenuFor === color.color_sku"
                    @update:open="(v) => onMenuOpenChange(color.color_sku, v)"
                    @assign-variant="(v) => onMenuAssign(color.color_sku, v)"
                    @reset-to-auto="onMenuReset(color.color_sku)"
                    @close="onMenuClose"
                >
                    <template #activator="{ props: activatorProps }">
                        <button
                            type="button"
                            v-bind="activatorProps"
                            :class="['filmstrip-variant-badge', `vtag-${cssTagFor(color)}`]"
                            :data-testid="`filmstrip-badge-${color.color_sku}`"
                            :data-variant-kind="kindFor(color) || ''"
                            :data-owned-by="ownerLabelFor(color) || ''"
                            :aria-label="ariaLabelFor(color)"
                            :title="pipTitleFor(color)"
                            @click.stop
                        >
                            {{ badgeTextFor(color) }}
                        </button>
                    </template>
                </VariantMenu>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useProductEditorStore } from "@/stores/productEditor";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import { classifyLDWithName } from "@/utils/luminance";
import { useVariantResolver } from "@/composables/useVariantResolver";
import { useArtifiCoverage } from "@/composables/useArtifiCoverage";
import { artifiPreviewEligible } from "@/utils/supplier-entitlement";
import {
    variantKindLabel,
    variantKindCssTag,
    variantKindBadgeText,
} from "@/utils/variantKindLabels";
import {
    VARIANT_KIND_CATCH_ALL_DARK,
    VARIANT_KIND_CATCH_ALL_LIGHT,
} from "@/utils/decorationRouting";
import { fallbackHex } from "@/constants/colorHexFallback";
import VariantMenu from "./VariantMenu.vue";

const editor = useProductEditorStore();
const editorUi = useProductEditorUiState();
const { resolve: resolveVariantForColor } = useVariantResolver();

// Artifi coverage gap surfacing (2026-06-19): the product_item_number
// drives a fetch against /api/artifi/supported_color_variants. Swatches
// for colors Artifi has no variant for get a "no preview" visual
// treatment + tooltip so the user understands the gap BEFORE clicking.
// Fail-open: a missing or errored coverage list does NOT dim any swatch
// (dimming a real color is more misleading than not dimming an uncovered
// one). The Stage's structured-404 fallback still catches the
// post-click case.
const productItemNumber = computed(
    () => editor.serverState?.data?.attributes?.product_item_number ?? null,
);

// Supplier gate (2026-07-10, Jake -- P0 regression fix). Only SanMar-backed
// products have Artifi coverage behind the supported_color_variants
// endpoint; firing it for a NAMED non-SanMar product 502s with no payoff
// (and no eye would ever show anyway). The gate keys on the effective
// supplier_name via isSanmarSupplierName -- SanMar-named OR unknown/unset
// supplier is eligible. This REPLACES the old is_smart_product gate, which
// wrongly skipped real SanMar products whose flag is false/null (the P0 this
// repairs). Mirrors StorefrontListingPanel.vue. See @/utils/isSanmarSupplier.
// Keyed on the resolved supplier_key since 2026-10-07 (S&S previews): see
// artifiPreviewEligible in @/utils/supplier-entitlement.
const isArtifiEligible = computed(() =>
    artifiPreviewEligible({
        supplierKey: editor.supplierKey,
        supplierKeyResolved: editor.supplierKeyResolved,
        supplierName: editor.getEffectiveAttribute?.("supplier_name"),
    }),
);

// SKU fed to the Artifi-coverage fetch: the product SKU for a SanMar-eligible
// product, null otherwise. useArtifiCoverage's watcher skips the fetch on
// a null/non-string SKU, so a named non-SanMar product never hits the endpoint.
const artifiCoverageSku = computed(() =>
    isArtifiEligible.value ? productItemNumber.value : null,
);
const { coverageFootnote } = useArtifiCoverage(artifiCoverageSku);

// Unrenderable-colors blueprint 5.3 (Phase 6): UNIFY, don't duplicate.
// isColorUnsupported now calls the SHARED coverageFootnote determination
// rule (the SAME function ColorsPanel/Stage use) instead of its own
// independent SKU-only isUnsupported check. The existing slashed-eye
// visual now means EXACTLY "every known view for this color fails" --
// matching the grid's determination rule. coverageFootnote is fail-open
// by construction (returns null on no data), so this preserves the
// existing "never dim a color we cannot prove is unsupported" contract.
function colorFootnoteFor(color) {
    const name = color?.name ?? color?.color_sku;
    if (!name) return null;
    return coverageFootnote(name);
}

function isColorUnsupported(color) {
    const footnote = colorFootnoteFor(color);
    return !!footnote && footnote.allFail;
}

// NEW (blueprint 5.3): partial-failure indicator -- coverageFootnote is
// non-null but allFail is false (some views fail, others pass or are
// still unprobed). Distinct, less-alarming visual from the slashed-eye.
function isColorPartiallyUnsupported(color) {
    const footnote = colorFootnoteFor(color);
    return !!footnote && !footnote.allFail;
}

// Swatch tooltip (blueprint 5.3): extended so the allFail and partial
// cases both name the failing views, using the SAME text composition as
// the ColorsPanel footnote list and the Stage removed-notice -- the three
// surfaces must never disagree about a given color's state. allFail keeps
// the existing "preview not yet available" message; partial appends the
// failing-views clause to the plain color name.
function swatchTitle(color) {
    const name = color.name || color.color_sku;
    const footnote = colorFootnoteFor(color);
    if (footnote?.allFail) {
        return `${name} -- preview not yet available for this color in Artifi (still orderable)`;
    }
    if (footnote && !footnote.allFail) {
        return `${name} -- no preview on ${footnote.failingViews.join(", ")}`;
    }
    return name;
}

const openMenuFor = ref(null);

// Day 3: always-visible. Prior `visible` computed (gated on
// editorUi.selectedPlacement != null) is removed — the header + swatch
// strip are now part of the persistent Editor chrome.

// Resolve selected_color_skus -> supplierMeta.colors rows. Preserves the
// supplier ordering and drops skus that don't appear in the supplier
// catalog (defensive; shouldn't happen for valid carts but keeps the
// filmstrip from rendering ghost swatches with no hex).
//
// Day 3 smoke fix: when supplierMeta is empty (e.g. seed data uses a
// fake SKU that doesn't resolve against the live SanMar catalog), we
// still want a visible swatch per selected_color_sku so the filmstrip
// renders. Fall back to building swatch entries from the SKU strings
// alone, with a neutral gray hex and the SKU as both name and value.
const selectedColors = computed(() => {
    const rawSkus = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    if (!rawSkus.length) return [];
    // Loading-race guard (2026-07-09): supplierMeta is null both before a
    // fetch starts and while one is in flight. Resolving against a null
    // meta here would take the synthesized-fallback branch below and render
    // a wrong-colored swatch per sku (e.g. "Coal Grey", absent from the
    // fallback map, showed a placeholder instead of its real #3E4241).
    // While meta is not ready, render nothing -- the strip shows an empty
    // loading state, and re-resolves once the real catalog arrives. The
    // synthesized-fallback path below stays reserved for colors genuinely
    // absent from a LOADED catalog.
    if (!editor.supplierMetaReady) return [];
    // Day 3 gate round-1 P1: dedup before rendering. selected_color_skus
    // comes from a user-edited attribute that, under partial-save races
    // or list-rewrite bugs, can contain repeats. Repeats produce
    // duplicate :key values in the v-for and Vue's reconciler then
    // throws an "Duplicate keys detected" warning + may reuse the wrong
    // DOM node when swatches are clicked.
    const skus = [...new Set(rawSkus)];
    const skuSet = new Set(skus);
    const all = editor.supplierMeta?.colors ?? [];
    const resolved = all.filter((c) => skuSet.has(c.color_sku));

    // If supplierMeta resolved everything, use it.
    if (resolved.length === skus.length) return resolved;

    // Otherwise build a fallback list preserving the user's selection
    // order, with resolved supplierMeta rows where available and
    // synthesized rows for the rest. A synthesized row is "Mode A" -- the
    // color isn't in the live SanMar catalog for this style -- so derive a
    // hex from the generic-name fallback map instead of rendering grey, and
    // flag it so the swatch shows the "approximate" affordance.
    const resolvedBySku = new Map(resolved.map((c) => [c.color_sku, c]));
    return skus.map((sku) => {
        const hit = resolvedBySku.get(sku);
        if (hit) return hit;
        const fb = fallbackHex(sku);
        return {
            color_sku: sku,
            name: sku,
            hex: fb, // null only if the name is in no fallback either
            is_hex_fallback: fb != null,
        };
    });
});

// PR-N2: per-color resolution is now kind-aware.
//
// Tier 1 activePreview: resolve against the EFFECTIVE placement (the
// selection, else the FIRST placement -- the same value the Stage
// renders) so the swatch badge and the Stage composite can never
// describe different placements. Only a product with NO placements
// falls back to the L/D heuristic so the badge is never blank. The
// badge text + CSS tag come from the variantKindLabels helper so the
// strings stay consistent across RoutingStrip / ColorFilmstrip /
// DecorationInspector / VariantMenu.
function resolvedVariantFor(color) {
    const p = editorUi.effectivePlacement;
    if (!p) return null;
    const placementId = p.id ?? p._tempId;
    return resolveVariantForColor(placementId, color.color_sku);
}

// Slice 4: the live variants on the effective placement -- the menu
// enumerates one "Assign to <variant>" option per entry. Empty when no
// placement is active or it has no variants (menu then shows only Reset).
const placementVariants = computed(() => {
    const p = editorUi.effectivePlacement;
    if (!p || !Array.isArray(p.variants)) return [];
    return p.variants;
});

// Slice 4: the id of the variant that CURRENTLY routes this color (the
// resolver's answer). Drives the menu's "current" marker + disables the
// already-owning option. null when the color is uncovered (pure auto).
function currentVariantIdFor(color) {
    const v = resolvedVariantFor(color);
    return v ? (v.id ?? v._tempId ?? null) : null;
}

// Slice 4: human label of the variant that currently OWNS this color --
// the steal-tooltip source. Only surfaced for colors that resolve to a
// real placement variant (not the auto L/D fallback, which is not a
// "steal from" case). null otherwise.
function ownerLabelFor(color) {
    const v = resolvedVariantFor(color);
    if (!v) return null;
    const custom = typeof v.name === "string" ? v.name.trim() : "";
    return (
        custom
        || variantKindLabel(v, { productColors: selectedColors.value })
        || null
    );
}

function kindFor(color) {
    const v = resolvedVariantFor(color);
    if (v) return v.kind;
    // No placement, or placement has no routing variant -- fall back
    // to the auto L/D heuristic so the badge is never blank.
    const { ld } = classifyLDWithName({ hex: color.hex, name: color.name });
    if (ld === "light") return VARIANT_KIND_CATCH_ALL_LIGHT;
    if (ld === "dark") return VARIANT_KIND_CATCH_ALL_DARK;
    return null;
}

function cssTagFor(color) {
    return variantKindCssTag({ kind: kindFor(color) });
}

function badgeTextFor(color) {
    return variantKindBadgeText({ kind: kindFor(color) });
}

function labelFor(color) {
    const v = resolvedVariantFor(color);
    if (v) {
        return variantKindLabel(v, { productColors: selectedColors.value });
    }
    return variantKindLabel({ kind: kindFor(color) });
}

function ariaLabelFor(color) {
    const lbl = labelFor(color);
    const name = color.name || color.color_sku;
    return lbl ? `${name}: ${lbl}` : `Variant routing for ${name}`;
}

function badgeTitleFor(color) {
    return labelFor(color) || "Variant routing";
}

// Slice 4: the pip's tooltip. When the color currently belongs to a real
// placement variant, surface the steal warning ("Currently on <owner> --
// assigning here moves it") so the user sees the consequence at the point
// of action (converged design 4). Falls back to the plain routing label
// when the color is uncovered / auto.
function pipTitleFor(color) {
    const owner = ownerLabelFor(color);
    if (owner) {
        return `Currently on ${owner} -- assigning here moves it.`;
    }
    return badgeTitleFor(color);
}

function swatchAriaLabel(color) {
    // Announce when a swatch's color is approximate (hex came from the
    // generic fallback map, not real SanMar/vendor data) so the affordance
    // is conveyed to assistive tech, not just sighted users.
    const name = color.name || color.color_sku;
    return color.is_hex_fallback ? `${name} (approximate color)` : name;
}

function swatchStyle(color) {
    if (color.hex) {
        return { background: color.hex };
    }
    if (color.swatch_url) {
        return {
            backgroundImage: `url(${color.swatch_url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
        };
    }
    return { background: "var(--tf-bg-muted, #f3f4f6)" };
}

function onSwatchClick(sku) {
    editorUi.setSelectedColor(sku);
}

// Slice 0 (editable-variant-cards): the badge is the v-menu activator.
// Vuetify wires the activator's onClick to toggle the menu, and v-menu
// reports every open-state change (activator toggle, outside-click,
// Escape) through v-model. We track WHICH color's menu is open in a
// single ref so at most one menu shows at a time: opening color X's
// menu closes any other. This handler receives the menu's new open
// state and updates openMenuFor accordingly.
function onMenuOpenChange(sku, isOpen) {
    openMenuFor.value = isOpen ? sku : null;
}

function onManageColors() {
    // Day 3 stub: the "Manage colors" affordance is wired into
    // the header but does not navigate yet. A future phase will route
    // this to the Properties > Colors section (selected_color_skus
    // editor).
    //
    // Day 4 round-1 P2 follow-up: the button is now `disabled` in the
    // template with a tooltip. This handler is retained so the click
    // wiring stays in place when Phase 1 lights it up — at which point
    // we drop `disabled` + `title` and put the navigation here.
}

// Slice 4 (editable variant cards): the VariantMenu now enumerates every
// variant on the placement. Choosing one assigns THIS color to it with
// steal-silently semantics. The store's ``assignColor`` action owns the
// atomic write per target kind (converged design 5.5):
//   - catch-all target -> color_overrides[sku] = light|dark (tier-0) +
//     clear the sku from any specific owner (backend, one transaction).
//   - specific target  -> set_colors steal-transfer (backend).
//   - single catch-all -> clear all explicit routing for the sku.
// This REPLACES the old manual_ld write (PR-N2) -- assignment intent is
// consolidated onto color_overrides. manual_ld remains only for the
// LDPipPopover "reclassify lightness" concept, untouched here.
async function onMenuAssign(sku, variant) {
    const p = editorUi.effectivePlacement;
    if (!p || !variant) {
        openMenuFor.value = null;
        return;
    }
    const decorationId = p.id ?? p._tempId;
    const variantId = variant.id ?? variant._tempId;
    if (variantId == null) {
        openMenuFor.value = null;
        return;
    }
    await editor.assignColor({
        decorationId,
        variantId,
        colorSku: sku,
        targetKind: variant.kind,
    });
    openMenuFor.value = null;
}

function onMenuReset(sku) {
    // Slice 4: "Reset to auto" clears the color from ALL routing inputs
    // (color_overrides + manual_ld + any specific card's color_skus) via
    // the store's ``resetColorToAuto`` action so the color returns to pure
    // hex auto-classification.
    const p = editorUi.effectivePlacement;
    if (!p) {
        openMenuFor.value = null;
        return;
    }
    const decorationId = p.id ?? p._tempId;
    editor.resetColorToAuto({ decorationId, colorSku: sku });
    openMenuFor.value = null;
}

function onMenuClose() {
    openMenuFor.value = null;
}
</script>

<style scoped>
/* Container — matches design Standalone.html `.filmstrip` block. */
.color-filmstrip {
    margin: 0 20px 14px;
    background: var(--tf-bg-surface, #ffffff);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 12px;
    padding: 10px 14px;
    flex: 0 0 auto;
}

/* Header row — small-caps eyebrow + right-aligned Manage colors. */
.filmstrip-header {
    display: flex;
    align-items: center;
    gap: 7px;
    font: 600 10px Roboto, system-ui, sans-serif;
    color: var(--tf-fg-3);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 9px;
}

.filmstrip-header-icon {
    font-size: 13px;
    color: var(--tf-brand-primary, #119ae9);
}

.filmstrip-header-label {
    text-transform: uppercase;
}

.filmstrip-header-sep {
    margin: 0 2px;
}

.filmstrip-manage-colors {
    margin-left: auto;
    text-transform: none;
    letter-spacing: 0;
    color: var(--tf-brand-primary, #119ae9);
    font: 500 11px Roboto, system-ui, sans-serif;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

/* Day 4 round-1 P2 follow-up: disabled visual cue mirrors the Day 2
   EditorPagehead hide-pill — same muted color + not-allowed cursor so
   the affordance is visibly inert. */
.filmstrip-manage-colors:disabled {
    color: var(--tf-fg-5, #949494);
    cursor: not-allowed;
    opacity: 0.65;
}

.filmstrip-manage-colors .material-icons {
    font-size: 13px;
}

/* Swatch strip. */
.filmstrip-dots {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.filmstrip-cell {
    position: relative;
}

.filmstrip-swatch {
    position: relative;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.1s;
    box-sizing: border-box;
    display: block;
}

.filmstrip-swatch:hover {
    transform: translateY(-2px);
}

.filmstrip-swatch.is-selected {
    box-shadow:
        0 0 0 2px #fff,
        0 0 0 4px var(--tf-brand-primary, #119ae9);
}

/* Approximate-color affordance: the hex came from the generic fallback
   map, not real SanMar/vendor data. A subtle dashed border + a small
   corner dot signal "generated, not exact" without shouting. */
.filmstrip-swatch.is-hex-fallback {
    border-style: dashed;
    border-color: rgba(0, 0, 0, 0.35);
}

.filmstrip-swatch.is-hex-fallback::after {
    content: "";
    position: absolute;
    top: 2px;
    right: 2px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4);
    pointer-events: none;
}

.filmstrip-check {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    pointer-events: none;
}

/* Artifi coverage gap (2026-06-19): swatches whose colors Artifi has
   no variant for are dimmed and overlaid with a slashed-eye icon. The
   swatch remains clickable -- ordering is independent of preview
   support; the click falls into the Stage's structured-404 fallback,
   which displays a user-helpful message. */
.filmstrip-swatch.is-artifi-unsupported {
    opacity: 0.55;
}
.filmstrip-swatch.is-artifi-unsupported:hover {
    opacity: 0.85;
}
.filmstrip-no-preview {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    pointer-events: none;
}

/* Partial-preview indicator (blueprint 5.3, Phase 6): a small, less-
   alarming corner dot for a color where SOME views fail but not all --
   distinct from the full slashed-eye overlay (which now means "every
   known view fails"). Top-left corner keeps clear of the variant badge
   (bottom-right) and the check overlay (centered). */
.filmstrip-partial-preview {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f59e0b;
    border: 1px solid rgba(255, 255, 255, 0.85);
    pointer-events: none;
}

/* Variant badge — sits on the swatch (bottom-right anchor per design)
   and is the menu trigger. */
.filmstrip-variant-badge {
    position: absolute;
    bottom: -6px;
    right: -5px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font: 700 9px Roboto, system-ui, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #fff;
    cursor: pointer;
    padding: 0;
}

/* PR-N2: kind-aware badge tags replace the legacy A/B/C single-letter
   classes. Visual mapping:
     - vtag-light    : light catch-all (light garments, dark ink)
     - vtag-dark     : dark catch-all (dark garments, light ink)
     - vtag-single   : single catch-all (one artwork for everything)
     - vtag-specific : specific_colors (distributor-curated routing)
     - vtag-none     : no variant routes (uncovered)

   vtag-specific is RETAINED after the 2026-08-24 light/dark-only scope
   cut even though the UI can no longer AUTHOR a specific_colors variant.
   The badge is a READ surface: variantKindCssTag() still returns
   "specific" for rows that already exist in the database, and the
   backend resolver tier still routes them. Deleting this rule would
   render those rows as an unstyled badge -- a visible regression on the
   read path the scope cut deliberately preserves.
*/
.filmstrip-variant-badge.vtag-light {
    background: #fff;
    color: #5a6a82;
    border-color: #d6d6d6;
}

.filmstrip-variant-badge.vtag-dark {
    background: #1a2332;
    color: #fff;
}

.filmstrip-variant-badge.vtag-single {
    background: var(--tf-brand-primary, #119ae9);
    color: #fff;
}

.filmstrip-variant-badge.vtag-specific {
    background: #f59e0b;
    color: #fff;
    border-color: #d97706;
}

.filmstrip-variant-badge.vtag-none {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fecaca;
}
</style>
